// PWA registration and lifecycle management

/**
 * Register the service worker for PWA functionality
 */
export const registerServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      
      if (registration.installing) {
        console.log('Service worker installing');
      } else if (registration.waiting) {
        console.log('Service worker installed');
      } else if (registration.active) {
        console.log('Service worker active');
      }
      
      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available, show update notification
              showUpdateNotification();
            }
          });
        }
      });
    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  }
};

/**
 * Show a notification when a new version of the app is available
 */
const showUpdateNotification = (): void => {
  // Create notification element
  const notification = document.createElement('div');
  notification.className = 'fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 flex justify-between items-center z-50';
  notification.innerHTML = `
    <div>A new version is available! Refresh to update.</div>
    <button class="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium">Refresh</button>
  `;
  
  // Add click handler
  notification.querySelector('button')?.addEventListener('click', () => {
    window.location.reload();
  });
  
  // Add to document
  document.body.appendChild(notification);
};

/**
 * Check if the app is running in standalone mode (installed PWA)
 */
export const isRunningAsStandalone = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (window.navigator as any).standalone === true;
};

/**
 * Check if the device is iOS
 */
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Check if the browser is Safari
 */
export const isSafari = (): boolean => {
  return /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
};

/**
 * Initialize PWA functionality
 */
export const initPWA = (): void => {
  registerServiceWorker();
  
  // Listen for app installed event
  window.addEventListener('appinstalled', (event) => {
    console.log('App was installed', event);
    // Hide any install prompts
    document.querySelectorAll('[data-pwa-install-prompt]').forEach(el => {
      el.remove();
    });
  });
};